import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ChatContext } from "../context/ChatContext";
import ConversationItem from "../components/ConversationItem";
import UserRegistration from "../components/UserRegistration";

const ConversationsPage = ({ isWidget = false }) => {
  const {
    conversations,
    createNewConversation,
    currentUser,
    registerUser,
    isCheckingUser,
    conversationsLoading,
    refreshConversations,
  } = useContext(ChatContext);
  const navigate = useNavigate();

  const handleNewChat = () => {
    const newConv = createNewConversation();
    navigate(`/chat/${newConv.id}`);
  };

  const handleConversationClick = (conversationId) => {
    // Navigate to the chat page with the conversation ID
    navigate(`/chat/${conversationId}`);
  };

  const handleUserRegistration = (userData) => {
    registerUser(userData);
    // After registration, start a new chat
    handleNewChat();
  };

  // Show loading while checking for existing user
  if (isCheckingUser) {
    return (
      <div
        className={`${
          isWidget ? "h-full" : "min-h-screen"
        } flex items-center justify-center`}
      >
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show registration form if no user
  if (!currentUser) {
    if (isWidget) {
      return (
        <UserRegistration onComplete={handleUserRegistration} isWidget={true} />
      );
    }
    return (
      <div className="min-h-screen pt-16 sm:pt-20">
        <div className="max-w-4xl mx-auto px-3 sm:px-4">
          <UserRegistration
            onComplete={handleUserRegistration}
            isWidget={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${
        isWidget
          ? "h-full flex flex-col overflow-hidden"
          : "min-h-screen pt-16 sm:pt-20 pb-10"
      } px-3 sm:px-4`}
    >
      <div
        className={`${
          isWidget
            ? "flex-grow flex flex-col overflow-hidden"
            : "max-w-5xl mx-auto"
        }`}
      >
        {/* Regular Header - Only show if not widget */}
        {!isWidget && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
                Your Conversations
              </h1>
              <p className="text-sm sm:text-base text-gray-400 mt-2">
                Continue exploring product solutions or start a new inquiry
              </p>
            </div>
          </div>
        )}

        {/* Widget Header */}
        {isWidget && (
          <div className="mb-4 text-center pt-4 flex-shrink-0">
            <p className="text-sm text-gray-300">How can we help you today?</p>
          </div>
        )}

        {conversationsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400 text-sm">Loading conversations...</p>
            </div>
          </div>
        ) : conversations.length === 0 ? (
          <div
            className={`glass rounded-2xl ${
              isWidget ? "p-6" : "p-8 sm:p-12"
            } text-center animate-fadeIn`}
          >
            <div
              className={`${
                isWidget
                  ? "w-14 h-14"
                  : "w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24"
              } mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-blue-400 to-purple-600 
                            rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30`}
            >
              <svg
                className={`${
                  isWidget
                    ? "w-7 h-7"
                    : "w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12"
                } text-white`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>

            <h2
              className={`${
                isWidget ? "text-lg" : "text-xl sm:text-2xl"
              } font-semibold text-white mb-3`}
            >
              No Conversations Yet
            </h2>
            <p
              className={`${
                isWidget ? "text-xs" : "text-sm sm:text-base"
              } text-gray-400 mb-6 sm:mb-8 max-w-md mx-auto px-4 sm:px-0`}
            >
              {isWidget
                ? "Start a conversation to get assistance"
                : "Start exploring our comprehensive catalog of monitoring and security solutions"}
            </p>

            <button
              onClick={handleNewChat}
              className={`${
                isWidget
                  ? "px-4 py-2 text-sm"
                  : "px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base"
              } 
                         rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 
                         text-white font-semibold hover:brightness-125 transition-all duration-300 
                         transform hover:scale-105 shadow-lg hover:shadow-[0_0_30px_rgba(56,189,248,0.5)]`}
            >
              Start Your First Chat
            </button>
          </div>
        ) : (
          <div
            className={`${
              isWidget ? "flex-grow flex flex-col overflow-hidden" : ""
            }`}
          >
            {/* New Chat Button for Widget Mode */}
            {isWidget && (
              <button
                onClick={handleNewChat}
                className="w-full mb-3 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 
                           text-white text-sm font-semibold hover:brightness-125 transition-all duration-300 flex-shrink-0"
              >
                <span className="flex items-center justify-center">
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  New Conversation
                </span>
              </button>
            )}

            <div
              className={`${
                isWidget
                  ? "flex-grow overflow-y-auto overflow-x-hidden px-1 custom-scrollbar"
                  : ""
              } space-y-3 sm:space-y-4`}
            >
              {!isWidget && (
                <div className="flex justify-end mb-2">
                  <button
                    onClick={refreshConversations}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors duration-300 text-gray-400 hover:text-white"
                    title="Refresh conversations"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </button>
                </div>
              )}
              {conversations.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  onClick={
                    isWidget
                      ? () => handleConversationClick(conversation.id)
                      : undefined
                  }
                  isWidget={isWidget}
                />
              ))}
            </div>
          </div>
        )}

        {!isWidget && conversations.length > 0 && (
          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-gray-500 text-xs sm:text-sm">
              {conversations.length} conversation
              {conversations.length !== 1 ? "s" : ""} • All conversations are
              saved locally
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsPage;
